import {
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import * as echarts from 'echarts';
import React, { useEffect, useRef, useState } from 'react';
import { DASHBOARD_COLORS } from '../../../../constants';
import { ADMIN_TIME_RANGES, type AdminTimeRange } from '../constants';
import type { IAdminChartCardProps } from '../interfaces';

const NO_DATA_MESSAGES: Record<AdminTimeRange, string> = {
  Week: 'No data for the past week.',
  Month: 'No data for the last month.',
  '6 Months': 'No insights for 6 months.',
};

/**
 * HeroUI-styled chart card built 100% with Material-UI components and ECharts.
 * Fully responsive, with pill time toggles, metrics legend, and zero Tailwind dependencies.
 */
export const AdminChartCard = ({
  label,
  data,
  loading = false,
  colors = DASHBOARD_COLORS,
  type = 'donut',
  hideToggles = false,
  maxValue,
  barWidthOverride,
}: IAdminChartCardProps) => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState<AdminTimeRange>('Week');
  const chartRef = useRef<HTMLDivElement | null>(null);

  const selectedData = data?.[timeRange] ?? [];
  const totalValue = selectedData.reduce((sum, item) => sum + item.value, 0);

  const displayData =
    type === 'line' && selectedData.length === 1
      ? [{ label: '', value: 0 }, ...selectedData]
      : selectedData;

  useEffect(() => {
    if (!chartRef.current || loading || displayData.length === 0) return;

    const chart = echarts.init(chartRef.current);
    const resizeObserver = window.ResizeObserver
      ? new ResizeObserver(() => chart.resize())
      : null;

    const appleTooltipStyle = {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e2e8f0',
      borderWidth: 1,
      textStyle: { color: '#0f172a', fontFamily: theme.typography.fontFamily },
      extraCssText: 'box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); border-radius: 8px;',
    };

    let option: echarts.EChartsOption = {};

    if (type === 'donut') {
      option = {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: <b>{c}</b> ({d}%)',
          ...appleTooltipStyle,
        },
        series: [
          {
            name: label,
            type: 'pie',
            radius: ['52%', '76%'],
            center: ['50%', '50%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 6,
              borderColor: '#ffffff',
              borderWidth: 2,
            },
            label: {
              show: true,
              position: 'center',
              formatter: () => `{total|${totalValue}}\n{label|Total}`,
              rich: {
                total: {
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#18181b',
                  fontFamily: theme.typography.fontFamily,
                  lineHeight: 26,
                },
                label: {
                  fontSize: 11,
                  fontWeight: 500,
                  color: '#71717a',
                  fontFamily: theme.typography.fontFamily,
                  lineHeight: 14,
                },
              },
            },
            data: displayData.map((item, index) => ({
              value: item.value,
              name: item.label,
              itemStyle: { color: colors[index % colors.length] },
            })),
          },
        ],
      };
    } else if (type === 'bar') {
      option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const item = Array.isArray(params) ? params[0] : params;
            return `<span style="display:block; font-size:11px; color:#71717a;">${item.name}</span><span style="display:block; font-weight:700; font-size:13px; color:#18181b; margin-top:2px;">${item.value}</span>`;
          },
          ...appleTooltipStyle,
        },
        grid: {
          left: '3%',
          right: '5%',
          bottom: '8%',
          top: '8%',
          containLabel: true,
        },
        xAxis: {
          type: 'value',
          max: maxValue,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#f4f4f5', type: 'dashed' } },
          axisLabel: {
            color: '#71717a',
            fontSize: 11,
            fontFamily: theme.typography.fontFamily,
          },
        },
        yAxis: {
          type: 'category',
          data: displayData.map((item) => item.label),
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: '#27272a',
            fontSize: 11,
            fontWeight: 500,
            fontFamily: theme.typography.fontFamily,
            formatter: (val: string) => (val.length > 14 ? `${val.slice(0, 14)}...` : val),
          },
        },
        series: [
          {
            data: displayData.map((item, index) => ({
              value: item.value,
              itemStyle: { color: colors[index % colors.length] },
            })),
            type: 'bar',
            barWidth: barWidthOverride || 12,
            itemStyle: { borderRadius: [0, 6, 6, 0] },
          },
        ],
      };
    } else if (type === 'column') {
      option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            const item = Array.isArray(params) ? params[0] : params;
            return `<span style="display:block; font-size:11px; color:#71717a;">${item.name}</span><span style="display:block; font-weight:700; font-size:13px; color:#18181b; margin-top:2px;">${item.value}</span>`;
          },
          ...appleTooltipStyle,
        },
        grid: {
          left: '3%',
          right: '3%',
          bottom: '10%',
          top: '12%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: displayData.map((item) => item.label),
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#e4e4e7' } },
          axisLabel: {
            color: '#71717a',
            fontSize: 11,
            fontFamily: theme.typography.fontFamily,
          },
        },
        yAxis: {
          type: 'value',
          max: maxValue,
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#f4f4f5', type: 'dashed' } },
          axisLabel: {
            color: '#71717a',
            fontSize: 11,
            fontFamily: theme.typography.fontFamily,
          },
        },
        series: [
          {
            data: displayData.map((item, index) => ({
              value: item.value,
              itemStyle: { color: colors[index % colors.length] },
            })),
            type: 'bar',
            barWidth: barWidthOverride || 14,
            itemStyle: { borderRadius: [6, 6, 0, 0] },
          },
        ],
      };
    }

    chart.setOption(option);

    const handleResize = () => chart.resize();
    resizeObserver?.observe(chartRef.current);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
      chart.dispose();
    };
  }, [displayData, label, colors, loading, totalValue, type, maxValue, barWidthOverride, theme.typography.fontFamily]);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: '6px',
        border: '1px solid #e4e4e7',
        bgcolor: '#ffffff',
        boxShadow: 'none',
        height: { xs: 275, sm: 295, lg: 310 },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent sx={{ p: 2.5, pb: '20px !important', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header with Title and Segmented Range Buttons */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 1.5, minWidth: 0 }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              fontSize: '0.92rem',
              color: '#18181b',
              letterSpacing: '-0.01em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {label}
          </Typography>

          {!hideToggles && (
            <Box
              sx={{
                display: 'inline-flex',
                bgcolor: '#f4f4f5',
                p: '2px',
                borderRadius: '6px',
                border: '1px solid #e4e4e7',
                flexShrink: 0,
              }}
            >
              {ADMIN_TIME_RANGES.map((range) => {
                const isSelected = timeRange === range;
                return (
                  <Button
                    key={range}
                    size="small"
                    onClick={() => setTimeRange(range)}
                    disableRipple
                    sx={{
                      minHeight: 24,
                      minWidth: 'auto',
                      px: 1.25,
                      py: 0.25,
                      fontSize: '0.72rem',
                      fontWeight: isSelected ? 600 : 500,
                      borderRadius: '4px',
                      color: isSelected ? '#18181b' : '#71717a',
                      bgcolor: isSelected ? '#ffffff' : 'transparent',
                      boxShadow: isSelected
                        ? '0 1px 2px rgba(0,0,0,0.06)'
                        : 'none',
                      '&:hover': {
                        bgcolor: isSelected ? '#ffffff' : 'rgba(0,0,0,0.04)',
                        color: '#18181b',
                      },
                    }}
                  >
                    {range === '6 Months' ? '6M' : range}
                  </Button>
                );
              })}
            </Box>
          )}
        </Stack>

        {/* Chart + Legend Body */}
        {loading ? (
          <Skeleton variant="rounded" sx={{ flex: 1, width: '100%', borderRadius: '6px' }} />
        ) : selectedData.length > 0 ? (
          <Stack direction="row" spacing={2} sx={{ flex: 1, minHeight: 0, alignItems: 'center' }}>
            {/* ECharts Area */}
            <Box ref={chartRef} sx={{ flex: 1, minWidth: 0, height: '100%' }} />

            {/* Metrics Legend */}
            <Stack
              spacing={1}
              sx={{
                width: { xs: 0, sm: '34%' },
                display: { xs: 'none', sm: 'flex' },
                borderLeft: '1px solid #f4f4f5',
                pl: 1.5,
                maxHeight: '100%',
                overflowY: 'auto',
                justifyContent: 'center',
              }}
            >
              {selectedData.map((entry, index) => (
                <Stack
                  key={`${entry.label}-${index}`}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        bgcolor: colors[index % colors.length],
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.74rem',
                        color: '#52525b',
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {entry.label}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      color: '#18181b',
                      fontVariantNumeric: 'tabular-nums',
                      flexShrink: 0,
                    }}
                  >
                    {entry.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.disabled',
              fontSize: '0.8rem',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {NO_DATA_MESSAGES[timeRange] ?? 'No data to display.'}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
